import { DonutItem } from 'app/screens/Home/components/ThemeDonutChart';
import { formatMillionsUSD } from 'utils/investment';

/** What a chart measures: the number of Acciones or their estimated investment in USD. */
export type Metric = 'count' | 'investment';

/** One bar or slice of /actions/analytics/; `id` and `name` are null for the Acciones without a value. */
export interface Bucket {
  id: number | null;
  name: string | null;
  count: number;
  /** USD, a decimal string from the API. */
  investment: string | number;
  /** País buckets. */
  code?: string;
  /** Territorio buckets. */
  countries?: { code: string }[];
}

export interface Analytics {
  countries: Bucket[];
  territories: Bucket[];
  themes: Bucket[];
  terms: Bucket[];
  scopes: Bucket[];
  types: Bucket[];
}

export const metricValue = (bucket: Bucket, metric: Metric): number =>
  metric === 'count' ? bucket.count : Number(bucket.investment);

export const bucketLabel = (bucket: Bucket, emptyLabel: string): string => bucket.name ?? emptyLabel;

/** A name as lines of at most `max` characters (a longer word keeps its own line), for chart axis labels. */
export const wrapLabel = (label: string, max: number): string[] => label.split(' ').reduce<string[]>((lines, word) => {
  const last = lines[lines.length - 1];
  if (last && `${last} ${word}`.length <= max) {
    lines[lines.length - 1] = `${last} ${word}`;
  } else {
    lines.push(word);
  }
  return lines;
}, []);

const MILLION = 1000000;

/** A value as the chart axis shows it: the count as is, the investment in USD millions. */
export const axisValue = (value: number, metric: Metric): number => (metric === 'count' ? value : value / MILLION);

/** A tooltip value: the count, or the investment in USD millions. */
export const formatMetric = (value: number, metric: Metric): string =>
  metric === 'count' ? String(value) : `${formatMillionsUSD(value)} M`;

export const donutItems = (
  buckets: Bucket[], metric: Metric, emptyLabel: string, colorOf: (index: number) => string
): DonutItem[] => buckets.map((bucket, index) => ({
  key: bucket.id ?? 'none',
  label: bucketLabel(bucket, emptyLabel),
  count: metricValue(bucket, metric),
  color: colorOf(index)
}));
